import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { wordArtId, BACA_LEVELS, BACA_LEVELS_EN, bacaLevel, bacaLevelEn, bacaLevelClips, bacaLevelClipsEn, bacaSyllables, clipFor as msClip, drillClip as msDrill, enClipFor, kategoriFor, letterSentence, quizStars, syllableClips, type ClipKind, type BacaLevel, type BacaStory, type BacaWord } from "@jomcerdik/shared";
import { api } from "../../../lib/api";
import { learnerKey } from "../../../lib/learnerId";
import { getLang, t, tk, useLang } from "../../../lib/i18n";
import { speak, useVoice } from "../../../lib/speak";
import { loadManifest, playClip, playQueue, useAudioSource } from "../../../lib/bacaAudio";
import { Card, Screen, Stars, StudyGate, TopBar } from "../kit";
import { Burst, shuffle } from "../games/common";
import { Art, Mascot } from "../../../ui/Art";
import { Banner, SectionTitle, pastel } from "../../../ui/kit";
import { Icon, ListenDot } from "../../../ui/Icon";

/* JomBaca Tadika — the reading content is Bahasa Melayu by design; only the UI chrome is translated. */

type Progress = Record<string, { best: number; plays: number }>;

const TADIKA_GAMES = [
  { to: "/belajar/tadika/kira", art: "nombor/bintang", title: tk("Kira Gambar"), text: tk("Matematik tadika: kira dan tambah."), tone: "from-sky-400 to-indigo-500" },
  { to: "/belajar/tadika/jawi", art: "mascot/boy-reading", title: tk("Celik Jawi"), text: tk("Kenal huruf Jawi dan bunyinya."), tone: "from-emerald-400 to-teal-600" },
  { to: "/belajar/tadika/mewarna", art: "mascot/girl-paint", title: tk("Mewarna"), text: tk("Pilih warna, tekan gambar."), tone: "from-pink-400 to-orange-400" },
  { to: "/belajar/tadika/belon", art: "nombor/belon", title: tk("Pecah Belon"), text: tk("Matematik: tambah dan tolak."), tone: "from-rose-400 to-red-500" },
  { to: "/belajar/tadika/sains", art: "alam/pokok", title: tk("Asingkan Sains"), text: tk("Sains: haiwan, tumbuhan dan pancaindera."), tone: "from-lime-400 to-emerald-600" },
  { to: "/belajar/tadika/eja", art: "huruf/huruf-a", title: tk("Eja Perkataan"), text: tk("Susun huruf untuk eja perkataan."), tone: "from-violet-400 to-purple-600" },
  { to: "/belajar/tadika/padan", art: "permainan/padan", title: tk("Padan Kad"), text: tk("Cari pasangan yang sama."), tone: "from-cyan-400 to-sky-600" },
  { to: "/belajar/tadika/garis/warna", art: "mascot/girl-paint", title: tk("Padan Warna"), text: tk("Tarik garisan: padankan warna dengan namanya."), tone: "from-pink-400 to-orange-400" },
  { to: "/belajar/tadika/garis/huruf", art: "huruf/huruf-a", title: tk("Besar dan Kecil"), text: tk("Tarik garisan: padankan huruf kecil dengan huruf besar."), tone: "from-amber-300 to-orange-500" },
  { to: "/belajar/tadika/garis/haiwan", art: "haiwan/gajah", title: tk("Padan Binatang"), text: tk("Tarik garisan: sambung dua bahagian binatang."), tone: "from-lime-400 to-green-600" },
  { to: "/belajar/tadika/garis/pekerjaan", art: "pekerjaan/doktor", title: tk("Padan Pekerjaan"), text: tk("Tarik garisan: padankan gambar dengan nama pekerjaan."), tone: "from-indigo-400 to-blue-600" },
  { to: "/belajar/tadika/garis/kenderaan", art: "kenderaan/kereta", title: tk("Padan Kenderaan"), text: tk("Tarik garisan: padankan gambar dengan nama kenderaan."), tone: "from-cyan-400 to-sky-600" },
  { to: "/belajar/tadika/garis/buah", art: "buah/epal", title: tk("Padan Buah"), text: tk("Tarik garisan: padankan gambar dengan nama buah."), tone: "from-yellow-400 to-red-500" },
] as const;

/** Reading content follows the app language: Bahasa Melayu for BM, English for every other language (no mixing). */
export const isEn = () => getLang() !== "ms";
export const contentLang = (): "ms" | "en" => (isEn() ? "en" : "ms");
const clipFor = (kind: ClipKind, text: string) => (isEn() ? enClipFor(kind === "line" ? "line" : "word", text) : msClip(kind, text));
const drillClip = (s: string[], word: string) => (isEn() ? enClipFor("word", word) : msDrill(s, word));
const wArt = (w: BacaWord) => w.art ?? wordArtId(w.w);
const levelsFor = () => (isEn() ? BACA_LEVELS_EN : BACA_LEVELS);
const levelOf = (n: number) => (isEn() ? bacaLevelEn(n) : bacaLevel(n));

/** Sound for a word card / picture: "ba, ju… baju" as one clip. */
const wordDrill = (w: BacaWord) => (w.s.length > 1 ? drillClip(w.s, w.w) : clipFor("word", w.w));
const sayWord = (w: BacaWord) => playClip(wordDrill(w), w.s.length > 1 ? `${w.s.join(", ")}. ${w.w}` : w.w, 0.7);

/** True when this device can make a correct sound: real audio exists, or a Malay/Indonesian voice. */
export function useHear(): boolean {
  const v = useVoice();
  const [audio, setAudio] = useState(false);
  useEffect(() => { void loadManifest().then((m) => setAudio(Object.keys(m).length > 0)); }, []);
  return audio || v.state === "ok" || isEn(); // English falls back to the device's English voice
}
const starsOf = (best?: number) => (best === undefined ? 0 : quizStars(Math.round(best * 10), 10));

export function TadikaHomePage() {
  return <Screen><StudyGate><TadikaHome /></StudyGate></Screen>;
}

const bestOf = (id: string) => { try { return Math.min(1, Number(localStorage.getItem(learnerKey(`tadika.kat.${id}`)) ?? 0) / 8); } catch { return 0; } };

/** Menu card in the PDF style: a picture of the category with the blue listen button, its name and one line, and a progress bar with a count. */
function MenuCard({ to, name, sub, art, n, best = 0 }: { to: string; name: string; sub: string; art: string; n: number; best?: number }) {
  const count = Math.round(best * 8);
  return (
    <Link to={to} className={`${pastel(n)} group block overflow-hidden rounded-3xl shadow-md transition hover:-translate-y-1 active:scale-95`}>
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-white/50">
        <Art id={art} alt="" className="h-full w-full object-cover transition group-hover:scale-105" />
        <div className="absolute bottom-2 right-2"><ListenDot onClick={() => (getLang() === "ms" ? speak(name, 0.7) : playClip(enClipFor("word", name), name, 0.7))} label={`${t("Dengar")} ${name}`} /></div>
      </div>
      <div className="bg-white p-3">
        <p className="text-lg font-black leading-tight text-slate-900">{name}</p>
        <p className="mt-0.5 min-h-8 text-xs leading-snug text-slate-600">{sub}</p>
        <div className="mt-2 flex items-center gap-2">
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-200" role="progressbar" aria-label={t("Kemajuan")} aria-valuenow={Math.round(best * 100)} aria-valuemin={0} aria-valuemax={100}><div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.round(best * 100)}%` }} /></div>
          <span className="text-xs font-bold text-slate-600">{count}/8</span>
        </div>
      </div>
    </Link>
  );
}

function TadikaHome() {
  const [progress, setProgress] = useState<Progress>({});
  const appLang = useLang();
  const KATEGORI = useMemo(() => kategoriFor(appLang === "ms" ? "ms" : "en"), [appLang]);
  const bests = KATEGORI.map((c) => bestOf(c.id));
  const done = bests.filter((b) => b > 0).length;
  const totalStars = bests.reduce((n, b) => n + quizStars(Math.round(b * 8), 8), 0);
  useEffect(() => { api<{ levels: Progress }>("/api/learn/baca/progress").then((r) => setProgress(r.levels)).catch(() => undefined); }, []);
  return (
    <>
      <Banner art="cover/tadika-hero" kicker="Jom Cerdik" title="Tadika" tagline={t("Belajar · Bermain · Jadi Lebih Bijak")} />
      <div className="mb-4 grid gap-3 md:grid-cols-3">
        <div className="flex items-center gap-3 rounded-3xl bg-white p-3 shadow-sm ring-1 ring-sky-100">
          <span className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-full bg-sky-100"><Mascot pose="boy-wave" className="h-14 w-14" /></span>
          <div><p className="text-lg font-black text-slate-900">{t("Hai, Adik!")}</p><p className="text-sm text-slate-600">{t("Teruskan belajar hari ini")}</p></div>
        </div>
        <div className="flex items-center gap-3 rounded-3xl bg-white p-3 shadow-sm ring-1 ring-sky-100">
          <Icon name="star" className="h-10 w-10 shrink-0 text-amber-400" />
          <div className="min-w-0 flex-1">
            <div className="flex justify-between text-sm font-black text-slate-900"><span>{t("Kemajuan Pembelajaran")}</span><span className="text-slate-600">{t("{a} / {b} modul", { a: done, b: KATEGORI.length })}</span></div>
            <div className="mt-1 h-3 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${(done / KATEGORI.length) * 100}%` }} /></div>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-3xl bg-white p-3 shadow-sm ring-1 ring-sky-100">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-500"><Icon name="trophy" className="h-8 w-8" /></span>
          <div><p className="text-sm font-bold text-slate-600">{t("Bintang Terkumpul")}</p><p className="flex items-center gap-1 text-2xl font-black text-slate-900">{totalStars} <Icon name="star" className="h-6 w-6 text-amber-400" /></p></div>
        </div>
      </div>
      <Card className="mb-4 bg-amber-50 text-sm text-amber-900 ring-amber-200 dark:bg-amber-950/30 dark:text-amber-200">
        <p>{t("Untuk ibu bapa: duduk bersama anak. Tekan suku kata untuk dengar bunyinya, minta anak sebut semula, kemudian main permainan di setiap tahap.")}</p>
      </Card>
      <VoiceBanner />
      <SectionTitle><Icon name="book" className="mr-2 inline h-6 w-6 text-brand" />{t("Modul Pembelajaran")}</SectionTitle>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {KATEGORI.map((c, n) => <MenuCard key={c.id} to={`/belajar/tadika/kategori/${c.id}`} name={c.ms} sub={t(c.blurb)} art={`cover/${c.id}`} n={n} best={bestOf(c.id)} />)}
        {([["Suku kata", "Baca suku kata dan perkataan.", "/belajar/tadika/1", "cover/suku-kata"], ["Permainan dan kuiz", "Main sambil belajar.", "/belajar/tadika/kira", "cover/permainan"]] as const).map(([name, sub, to, art], n) => (
          <MenuCard key={to} to={to} name={t(name)} sub={t(sub)} art={art} n={n + KATEGORI.length} />
        ))}
      </div>
      <SectionTitle>{t("Permainan Tadika")}</SectionTitle>
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {TADIKA_GAMES.map((g) => (
          <Link key={g.to} to={g.to} className={`relative flex items-center gap-3 overflow-hidden rounded-3xl bg-gradient-to-br ${g.tone} p-4 text-white shadow-md transition hover:-translate-y-0.5`}>
            <Art id={g.art} alt="" className="h-16 w-16 shrink-0" />
            <span><span className="block text-lg font-extrabold leading-tight">{t(g.title)}</span><span className="block text-xs opacity-90">{t(g.text)}</span></span>
          </Link>
        ))}
      </div>
      <SectionTitle>{t("Jom Baca: 8 tahap")}</SectionTitle>
      <ol className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {levelsFor().map((l) => {
          const p = progress[String(l.level)];
          return (
            <li key={l.level}>
              <Link to={`/belajar/tadika/${l.level}`} className={`relative block h-full overflow-hidden rounded-3xl bg-gradient-to-br ${l.tone} p-4 text-white shadow-md transition hover:-translate-y-0.5`}>
                <span className="pointer-events-none absolute -right-2 -top-3 text-6xl font-black opacity-20">{l.level}</span>
                <Icon name="book" className="h-8 w-8" />
                <p className="mt-1 text-xs font-bold uppercase tracking-wide opacity-90">{t("Tahap {n}", { n: l.level })}</p>
                <p className="text-base font-extrabold leading-tight">{l.title}</p>
                <div className="mt-2 rounded-full bg-white/25 px-2 py-0.5 text-center"><Stars n={starsOf(p?.best)} size="text-base" /></div>
              </Link>
            </li>
          );
        })}
      </ol>
    </>
  );
}

export function TadikaLevelPage() {
  return <Screen><StudyGate><LevelScreen /></StudyGate></Screen>;
}

type Mode = { kind: "learn" } | { kind: "cantum" } | { kind: "pilih" } | { kind: "cerita"; story: BacaStory };

function LevelScreen() {
  const n = Number(useParams().level);
  useLang();
  const level = levelOf(n);
  const [mode, setMode] = useState<Mode>({ kind: "learn" });
  const clips = useMemo(() => (level ? (isEn() ? bacaLevelClipsEn(level) : bacaLevelClips(level)) : []), [level]);
  useAudioSource(clips); // downloads this level's audio in the background
  useEffect(() => { setMode({ kind: "learn" }); window.scrollTo(0, 0); }, [n]);
  if (!level) return <Card>{t("Tahap tidak dijumpai.")} <Link className="underline" to="/belajar/tadika">{t("Kembali")}</Link></Card>;
  const back = () => { setMode({ kind: "learn" }); window.scrollTo(0, 0); };

  return (
    <>
      <TopBar back={mode.kind === "learn" ? "/belajar/tadika" : undefined} title={`${t("Tahap {n}", { n: level.level })} · ${level.title}`} subtitle={level.focus} />
      {mode.kind !== "learn" && <button type="button" onClick={back} className="mb-3 rounded-full bg-card px-4 py-1.5 text-sm font-bold text-slate-700 ring-1 ring-black/10">‹ {t("Kembali ke tahap")}</button>}
      {mode.kind === "learn" && <Learn level={level} onMode={(m) => { setMode(m); window.scrollTo(0, 0); }} />}
      {mode.kind === "cantum" && <Cantum level={level} onDone={back} />}
      {mode.kind === "pilih" && <Pilih level={level} onDone={back} />}
      {mode.kind === "cerita" && <Cerita level={level} story={mode.story} onDone={back} />}
    </>
  );
}

/* ── learn: syllables, words, sentences, stories ─────────────────────────── */
function Learn({ level, onMode }: { level: BacaLevel; onMode: (m: Mode) => void }) {
  const next = levelOf(level.level + 1);
  return (
    <div className="space-y-6">
      {level.groups.length > 0 && (
        <section>
          <h2 className="mb-2 text-lg font-extrabold text-slate-900">{isEn() ? (level.groupsTitle ?? "Letters") : t("Suku kata")} <SpeakHint /></h2>
          <div className="space-y-2">
            {level.groups.map((g) => (
              <div key={g.label} className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                <span className="w-full text-xs font-bold uppercase text-slate-400 sm:w-14">{g.label}</span>
                {g.items.map((s) => <SyllableTile key={s} text={s} />)}
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="mb-2 text-lg font-extrabold text-slate-900">{t("Perkataan")}</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {level.words.map((w) => <WordCard key={w.w} w={w} />)}
        </div>
      </section>

      {level.sentences.length > 0 && (
        <section>
          <h2 className="mb-2 text-lg font-extrabold text-slate-900">{t("Baca ayat")}</h2>
          <ul className="space-y-2">{level.sentences.map((s) => <Sentence key={s} text={s} />)}</ul>
        </section>
      )}

      {level.stories && (
        <section>
          <h2 className="mb-2 text-lg font-extrabold text-slate-900">{t("Cerita")}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {level.stories.map((st) => (
              <button key={st.title} type="button" data-story-title={st.title} onClick={() => onMode({ kind: "cerita", story: st })}
                className="flex items-center gap-3 rounded-3xl bg-card p-4 text-left shadow-sm ring-1 ring-black/5">
                <Art id={st.art ?? wordArtId(st.e)} alt="" className="h-16 w-16 shrink-0" />
                <span><span className="block text-base font-extrabold text-slate-900">{st.title}</span><span className="text-xs text-slate-500">{t("Baca dan jawab {n} soalan", { n: st.questions.length })}</span></span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="mb-2 text-lg font-extrabold text-slate-900">{t("Jom main")}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <button type="button" onClick={() => onMode({ kind: "cantum" })} className="rounded-3xl bg-gradient-to-br from-violet-500 to-indigo-500 p-4 text-left text-white shadow-md">
            <Icon name="game" className="mx-auto h-8 w-8" /><p className="text-lg font-extrabold">{t("Cantum suku kata")}</p><p className="text-xs opacity-90">{t("Susun suku kata ikut gambar.")}</p>
          </button>
          <button type="button" onClick={() => onMode({ kind: "pilih" })} className="rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-500 p-4 text-left text-white shadow-md">
            <Icon name="pencil" className="mx-auto h-8 w-8" /><p className="text-lg font-extrabold">{t("Pilih gambar")}</p><p className="text-xs opacity-90">{t("Baca perkataan, tekan gambar yang betul.")}</p>
          </button>
        </div>
      </section>

      {next && <Link to={`/belajar/tadika/${next.level}`} className="block rounded-full bg-slate-900 py-3 text-center text-sm font-extrabold text-white">{t("Tahap seterusnya")}: {next.title} ›</Link>}
    </div>
  );
}

/** Which voice reads aloud — or why there is none and how to add one. */
function VoiceBanner() {
  const v = useVoice();
  const sample = useMemo(() => ["ca", "ci", "cu"].map((x) => msClip("syl", x)), []);
  const src = useAudioSource(sample);
  if (isEn()) return null;
  if (src === "loading" || (src === "device" && v.state === "loading")) return null;
  if (src !== "device")
    return (
      <p className="mb-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <Icon name="speaker" className="mr-1 inline h-4 w-4" /> {t("Audio sebutan JomCerdik")}
        <button type="button" data-audio-keys={sample.map(c=>c.key).join(",")} onClick={() => playQueue(sample, "ca, ci, cu")} className="rounded-full bg-card px-3 py-1 font-bold text-slate-700 ring-1 ring-black/10">{t("Uji suara")}</button>
      </p>
    );
  if (v.state === "ok")
    return (
      <p className="mb-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <Icon name="speaker" className="mr-1 inline h-4 w-4" /> {t("Suara: {name}", { name: `${v.name} (${v.lang})` })}
        <button type="button" data-audio-keys={sample.map(c=>c.key).join(",")} onClick={() => playQueue(sample, "ca, ci, cu")} className="rounded-full bg-card px-3 py-1 font-bold text-slate-700 ring-1 ring-black/10">{t("Uji suara")}</button>
      </p>
    );
  return (
    <Card className="mb-4 bg-rose-50 text-sm text-rose-900 ring-rose-200 dark:bg-rose-950/30 dark:text-rose-200">
      <p className="font-bold">{t("Telefon ini tiada suara Bahasa Melayu, jadi bunyi dimatikan supaya sebutan tidak salah.")}</p>
      <p className="mt-1">{t("iPhone/iPad: Settings → Accessibility → Spoken Content → Voices → Malay (atau Indonesian) → muat turun.")}</p>
      <p className="mt-1">{t("Android: Settings → Bahasa & input → Output teks-ke-pertuturan → Google → Pasang data suara → Bahasa Melayu (atau Indonesia).")}</p>
      <p className="mt-1">{t("Selepas itu tutup dan buka semula pelayar. Sementara itu, bacakan suku kata untuk anak.")}</p>
    </Card>
  );
}

function SpeakHint() {
  return useHear() ? <span className="ml-1 text-xs font-semibold text-slate-400"><Icon name="speaker" className="mr-1 inline h-4 w-4" /> {t("tekan untuk dengar")}</span> : null;
}

function SyllableTile({ text }: { text: string }) {
  const [pop, setPop] = useState(0);
  const parts = text.split("-");
  // English letter tile ("Aa"): says "A is for apple."; any other English tile is a word.
  const letter = isEn() && /^[A-Z][a-z]$/.test(text) && text[0]!.toLowerCase() === text[1];
  const clip = letter ? enClipFor("line", letterSentence(text[1]!)) : isEn() ? enClipFor("word", parts.join("")) : parts.length > 1 ? drillClip(parts, parts.join("")) : clipFor("syl", text);
  const say = letter ? letterSentence(text[1]!) : isEn() ? parts.join("") : parts.join(", ");
  return (
    <button type="button" key={pop} data-audio-key={clip.key} onClick={() => { setPop((p) => p + 1); playClip(clip, say, 0.7); }}
      className={`min-w-[3rem] rounded-2xl bg-card px-2.5 py-2 text-2xl font-extrabold text-slate-900 shadow-sm ring-1 ring-black/10 ${pop ? "animate-tap" : ""}`}>
      {parts.map((p, i) => <span key={i} className={i % 2 ? "text-sky-600" : "text-rose-600"}>{p}</span>)}
    </button>
  );
}

function Syllables({ s }: { s: string[] }) {
  return <>{s.map((p, i) => <span key={i} className={i % 2 ? "text-sky-600" : "text-rose-600"}>{p}</span>)}</>;
}

function WordCard({ w }: { w: BacaWord }) {
  const [pop, setPop] = useState(0);
  return (
    <button type="button" data-audio-key={wordDrill(w).key} onClick={() => { setPop((p) => p + 1); sayWord(w); }}
      className="rounded-3xl bg-card p-3 text-center shadow-sm ring-1 ring-black/5">
      <span key={pop} className={`block ${pop ? "animate-tap" : ""}`}><Art id={wArt(w)} alt={w.w} className="mx-auto h-24 w-24" /></span>
      <span className="mt-1 block text-2xl font-extrabold tracking-wide"><Syllables s={w.s} /></span>
      <span className="block text-xs text-slate-400">{w.s.join(" - ")}</span>
    </button>
  );
}

function Sentence({ text }: { text: string }) {
  const canSay = useHear();
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-2xl bg-card px-3 py-2 shadow-sm ring-1 ring-black/5">
      {canSay && <button type="button" data-audio-key={clipFor("line", text).key} aria-label={t("Dengar ayat")} onClick={() => playClip(clipFor("line", text), text, 0.75)} className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500 text-white"><Icon name="speaker" className="mr-1 inline h-4 w-4" /></button>}
      {text.split(" ").map((word, i) => (
        <button type="button" key={i} data-audio-key={clipFor("word", word).key} onClick={() => playClip(clipFor("word", word), word.replace(/[.,!?]/g, ""), 0.7)} className="rounded-lg px-1 text-2xl font-bold text-slate-900 hover:bg-amber-100">{word}</button>
      ))}
    </li>
  );
}

/* ── games ───────────────────────────────────────────────────────────────── */
export function useRecord(level: number, activity: string) {
  return (score: number, max: number) => {
    api("/api/learn/games/baca", { method: "POST", json: { score, max, details: { level, activity } } }).catch(() => undefined);
  };
}

export function Done({ score, max, onAgain, onDone, save }: { score: number; max: number; onAgain: () => void; onDone: () => void; save: (score: number, max: number) => void }) {
  useEffect(() => { save(score, max); }, []); // once per finished round
  const stars = quizStars(score, max);
  return (
    <Card className="animate-rise text-center">
      <Mascot pose={stars >= 2 ? "boy-cheer" : stars === 1 ? "girl-happy" : "boy-think"} className="mx-auto h-32 w-32" />
      <p className="mt-2 text-3xl font-extrabold text-slate-900">{score}/{max}</p>
      <Stars n={stars} size="text-3xl" />
      <p className="mt-1 text-sm text-slate-500">{stars >= 2 ? t("Pandai! Teruskan membaca.") : t("Cuba lagi. Anda pasti boleh!")}</p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        <button type="button" onClick={onAgain} className="rounded-full bg-violet-500 px-6 py-2.5 text-sm font-extrabold text-white shadow">{t("Main lagi")}</button>
        <button type="button" onClick={onDone} className="rounded-full bg-card px-6 py-2.5 text-sm font-bold text-slate-700 ring-1 ring-black/10">{t("Kembali ke tahap")}</button>
      </div>
    </Card>
  );
}

const ROUND = 6;

function Cantum({ level, onDone }: { level: BacaLevel; onDone: () => void }) {
  const [seed, setSeed] = useState(0);
  return <CantumRound key={seed} level={level} onAgain={() => setSeed((s) => s + 1)} onDone={onDone} />;
}

function CantumRound({ level, onAgain, onDone }: { level: BacaLevel; onAgain: () => void; onDone: () => void }) {
  const pool = useMemo(() => (isEn() ? [...new Set(level.words.flatMap((x) => x.s))] : bacaSyllables(level)), [level]);
  const deck = useMemo(() => shuffle(level.words.filter((w) => w.s.length > 1)).slice(0, ROUND), [level]);
  const [i, setI] = useState(0);
  const [filled, setFilled] = useState<string[]>([]);
  const [wrong, setWrong] = useState<string | null>(null);
  const [miss, setMiss] = useState(false);
  const [score, setScore] = useState(0);
  const [burst, setBurst] = useState(0);
  const record = useRecord(level.level, "cantum");
  const w = deck[i];
  const tiles = useMemo(() => {
    if (!w) return [];
    const extra = shuffle(pool.filter((p) => !w.s.includes(p))).slice(0, Math.min(3, 6 - w.s.length + 1));
    return shuffle([...w.s, ...extra].map((text, k) => ({ id: `${k}-${text}`, text })));
  }, [w, pool]);
  const [used, setUsed] = useState<string[]>([]);

  if (!w) return <Done score={score} max={deck.length} onAgain={onAgain} onDone={onDone} save={record} />;
  const complete = filled.length === w.s.length;

  function tap(tile: { id: string; text: string }) {
    if (complete || !w || used.includes(tile.id)) return;
    if (tile.text === w.s[filled.length]) {
      const nf = [...filled, tile.text];
      setFilled(nf); setUsed((u) => [...u, tile.id]);
      const syl = isEn() ? null : syllableClips(w.s, w.w)[filled.length]!;
      if (nf.length === w.s.length) {
        if (!miss) setScore((s) => s + 1);
        setBurst((b) => b + 1);
        playQueue(syl ? [syl, clipFor("word", w.w)] : [clipFor("word", w.w)], syl ? `${tile.text}. ${w.w}` : w.w); // one gesture → both sounds
      } else if (syl) playClip(syl, tile.text, 0.7);
    } else {
      setWrong(tile.id); setMiss(true); setTimeout(() => setWrong(null), 450);
    }
  }
  function next() { setI((k) => k + 1); setFilled([]); setUsed([]); setMiss(false); }

  return (
    <Card className="space-y-5 text-center">
      <Burst show={burst} />
      <p className="text-xs font-bold text-slate-400">{i + 1}/{deck.length} · <Icon name="star" className="inline h-4 w-4 text-amber-400" /> {score}</p>
      <button type="button" onClick={() => sayWord(w)} className="mx-auto block" aria-label={t("Dengar")}><Art id={wArt(w)} alt={w.w} className="mx-auto h-44 w-44" /></button>
      <div className="flex justify-center gap-2">
        {w.s.map((s, k) => (
          <span key={k} className={`grid h-16 min-w-[4rem] place-items-center rounded-2xl border-2 border-dashed px-2 text-3xl font-extrabold ${filled[k] ? "border-emerald-400 bg-emerald-50 text-emerald-700 animate-tap" : "border-slate-300 text-transparent"}`}>{filled[k] ?? s}</span>
        ))}
      </div>
      {complete ? (
        <div className="space-y-3">
          <p className="text-3xl font-extrabold text-emerald-600">{w.w}</p>
          <button type="button" onClick={next} className="w-full rounded-full bg-emerald-500 py-3 text-base font-extrabold text-white shadow-md">{t("Seterusnya")} ›</button>
        </div>
      ) : (
        <div className="flex flex-wrap justify-center gap-2">
          {tiles.map((tile) => (
            <button key={tile.id} type="button" disabled={used.includes(tile.id)} onClick={() => tap(tile)}
              className={`rounded-2xl bg-amber-100 px-4 py-3 text-2xl font-extrabold text-amber-900 shadow-sm ring-1 ring-amber-300 disabled:opacity-25 ${wrong === tile.id ? "animate-shake bg-rose-100 ring-rose-300" : ""}`}>{tile.text}</button>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400">{t("Tekan gambar untuk dengar perkataan.")}</p>
    </Card>
  );
}

function Pilih({ level, onDone }: { level: BacaLevel; onDone: () => void }) {
  const [seed, setSeed] = useState(0);
  return <PilihRound key={seed} level={level} onAgain={() => setSeed((s) => s + 1)} onDone={onDone} />;
}

function PilihRound({ level, onAgain, onDone }: { level: BacaLevel; onAgain: () => void; onDone: () => void }) {
  const others = useMemo(() => [...new Map(levelsFor().filter((l) => l.level <= level.level).flatMap((l) => l.words).map((x) => [x.w, x] as const)).values()], [level]);
  const deck = useMemo(() => shuffle(level.words).slice(0, Math.min(8, level.words.length)), [level]);
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [burst, setBurst] = useState(0);
  const record = useRecord(level.level, "pilih");
  const w = deck[i];
  const canSay = useHear();
  const opts = useMemo(() => (w ? shuffle([w, ...shuffle(others.filter((o) => o.w !== w.w)).slice(0, 2)]) : []), [w, others]);

  if (!w) return <Done score={score} max={deck.length} onAgain={onAgain} onDone={onDone} save={record} />;
  function tap(o: BacaWord) {
    if (picked || !w) return;
    setPicked(o.w);
    if (o.w === w.w) { setScore((s) => s + 1); setBurst((b) => b + 1); playClip(clipFor("word", w.w), w.w); }
    setTimeout(() => { setPicked(null); setI((k) => k + 1); }, 1100);
  }
  return (
    <Card className="space-y-5 text-center">
      <Burst show={burst} />
      <p className="text-xs font-bold text-slate-400">{i + 1}/{deck.length} · <Icon name="star" className="inline h-4 w-4 text-amber-400" /> {score}</p>
      <p className="text-5xl font-extrabold tracking-wide"><Syllables s={w.s} /></p>
      {canSay && <button type="button" onClick={() => sayWord(w)} className="rounded-full bg-emerald-500 px-4 py-1.5 text-sm font-bold text-white"><Icon name="speaker" className="mr-1 inline h-4 w-4" /> {t("Dengar")}</button>}
      <div className="grid grid-cols-3 gap-3">
        {opts.map((o) => {
          const state = !picked ? "" : o.w === w.w ? "bg-emerald-100 ring-emerald-400 animate-tap" : o.w === picked ? "bg-rose-100 ring-rose-300 animate-shake" : "opacity-40";
          return <button key={o.w} type="button" onClick={() => tap(o)} aria-label={o.w} className={`rounded-3xl bg-card py-4 shadow-sm ring-2 ring-black/5 ${state}`}><Art id={wArt(o)} alt={o.w} className="mx-auto h-24 w-24" /></button>;
        })}
      </div>
    </Card>
  );
}

function Cerita({ level, story, onDone }: { level: BacaLevel; story: BacaStory; onDone: () => void }) {
  const [quiz, setQuiz] = useState(false);
  const [qi, setQi] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const record = useRecord(level.level, `cerita:${story.title}`.slice(0, 80));
  const canSay = useHear();
  if (!quiz)
    return (
      <Card className="space-y-4">
        <Art id={story.art ?? wordArtId(story.e)} alt="" className="mx-auto h-32 w-32" />
        <h2 className="text-center text-2xl font-extrabold text-slate-900">{story.title}</h2>
        {canSay && <div className="text-center"><button type="button" data-audio-keys={story.lines.map(l=>clipFor("line",l).key).join(",")} onClick={() => playQueue(story.lines.map((l) => clipFor("line", l)), story.lines.join(" "))} className="rounded-full bg-emerald-500 px-4 py-1.5 text-sm font-bold text-white"><Icon name="speaker" className="mr-1 inline h-4 w-4" /> {t("Dengar cerita")}</button></div>}
        <ol className="space-y-2">{story.lines.map((l) => <Sentence key={l} text={l} />)}</ol>
        <button type="button" onClick={() => setQuiz(true)} className="w-full rounded-full bg-violet-500 py-3 text-base font-extrabold text-white shadow-md">{t("Jawab soalan")} ›</button>
      </Card>
    );
  const q = story.questions[qi];
  if (!q) return <Done score={score} max={story.questions.length} onAgain={() => { setQuiz(false); setQi(0); setScore(0); }} onDone={onDone} save={record} />;
  function tap(k: number) {
    if (picked !== null || !q) return;
    setPicked(k);
    if (k === q.answer) setScore((s) => s + 1);
    setTimeout(() => { setPicked(null); setQi((x) => x + 1); }, 1100);
  }
  return (
    <Card className="space-y-4 text-center">
      <p className="text-xs font-bold text-slate-400">{qi + 1}/{story.questions.length}</p>
      <p className="text-2xl font-extrabold text-slate-900">{q.q}</p>
      {canSay && <button type="button" data-audio-key={clipFor("line", q.q).key} onClick={() => playClip(clipFor("line", q.q), q.q)} className="rounded-full bg-emerald-500 px-4 py-1.5 text-sm font-bold text-white"><Icon name="speaker" className="mr-1 inline h-4 w-4" /></button>}
      <div className="grid gap-2">
        {q.options.map((o, k) => {
          const st = picked === null ? "" : k === q.answer ? "bg-emerald-100 ring-emerald-400" : k === picked ? "bg-rose-100 ring-rose-300 animate-shake" : "opacity-40";
          return <button key={o} type="button" data-quiz-choice={k} onClick={() => tap(k)} className={`rounded-2xl bg-card px-4 py-3 text-xl font-bold text-slate-900 ring-2 ring-black/5 ${st}`}>{o}</button>;
        })}
      </div>
    </Card>
  );
}

